import type { ProjectSummary, AllQuartersData, ProductsResponse, Product, QuarterDataItem, EpicCountItem, ReviewDataItem } from '../shared/types';
import { authenticatedFetch } from '../shared/authenticatedFetch';
import { ApiError } from '../shared/apiError';

export async function getProjectSummary(
  apiUrl: string,
  projectName: string,
  fiscalYearQuarter: string
): Promise<ProjectSummary | null> {
  const response = await authenticatedFetch(
    `${apiUrl}/projects/summary?projectName=${encodeURIComponent(projectName)}&fiscalYearQuarter=${encodeURIComponent(fiscalYearQuarter)}`
  );

  const data = await response.json();
  return data || null;
}

export async function fetchAllProducts(
  apiUrl: string,
  fiscalYear?: string
): Promise<Product[]> {
  const fiscalYearParam = fiscalYear ? `&fiscalYear=${encodeURIComponent(fiscalYear)}` : '';
  const response = await authenticatedFetch(`${apiUrl}/products?limit=1000${fiscalYearParam}`);
  const result: ProductsResponse = await response.json();
  return dedupeProductQuarters(result.items);
}

/**
 * Keeps one row per project/quarter pair. Products are returned per quarter, so
 * deduplicating by project name alone would leave each project with a single quarter.
 */
function dedupeProductQuarters(items: Product[]): Product[] {
  const seen = new Set<string>();
  const unique: Product[] = [];

  for (const item of items) {
    if (!item.project_name) continue;
    const key = `${item.project_name}|${canonicaliseQuarter(item.fiscal_year_quarter)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(item);
  }

  return unique;
}

/**
 * Fiscal year quarters are stored and returned in the canonical spaced form ("FY26 Q1").
 * An unspaced value ("FY26Q1") is only tolerated as inbound data and canonicalised here so
 * that keys are always spaced.
 */
export function canonicaliseQuarter(fiscalYearQuarter: string | null | undefined): string {
  const value = (fiscalYearQuarter ?? '').trim().toUpperCase();
  const match = /^FY\s*(\d{2})\s*Q\s*([1-4])$/.exec(value);
  return match ? `FY${match[1]} Q${match[2]}` : value;
}

export async function fetchEpicCounts(
  apiUrl: string,
  fiscalYearQuarters: string[]
): Promise<EpicCountItem[]> {
  try {
    const quartersParam = fiscalYearQuarters.map(q => encodeURIComponent(q)).join(',');
    const response = await authenticatedFetch(
      `${apiUrl}/projects/epic-counts?fiscalYearQuarters=${quartersParam}`
    );
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized) {
      throw error;
    }
    console.warn('Failed to fetch epic counts from backend:', error);
    return [];
  }
}

export async function fetchReviewData(
  apiUrl: string,
  fiscalYearQuarters: string[]
): Promise<ReviewDataItem[]> {
  try {
    const quartersParam = fiscalYearQuarters.map(q => encodeURIComponent(q)).join(',');
    const response = await authenticatedFetch(
      `${apiUrl}/projects/review-data?fiscalYearQuarters=${quartersParam}`
    );
    return await response.json();
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized) {
      throw error;
    }
    console.warn('Failed to fetch review data from backend:', error);
    return [];
  }
}

export function mergeQuarterData(
  epicCounts: EpicCountItem[],
  reviewData: ReviewDataItem[]
): QuarterDataItem[] {
  const merged = new Map<string, QuarterDataItem>();

  for (const ec of epicCounts) {
    const key = `${ec.project_name}|${canonicaliseQuarter(ec.fiscal_year_quarter)}`;
    merged.set(key, {
      project_name: ec.project_name,
      fiscal_year_quarter: canonicaliseQuarter(ec.fiscal_year_quarter),
      epic_count: ec.epic_count,
      review_status: null,
      review_id: null,
      final_capex_percentage: null,
      final_rnd_percentage: null,
      assessed_epic_count: 0,
    });
  }

  for (const rd of reviewData) {
    const key = `${rd.project_name}|${canonicaliseQuarter(rd.fiscal_year_quarter)}`;
    const existing = merged.get(key);
    if (existing) {
      existing.review_status = rd.review_status;
      existing.review_id = rd.review_id;
      existing.final_capex_percentage = rd.final_capex_percentage;
      existing.final_rnd_percentage = rd.final_rnd_percentage;
      existing.assessed_epic_count = rd.assessed_epic_count;
    } else {
      merged.set(key, {
        project_name: rd.project_name,
        fiscal_year_quarter: canonicaliseQuarter(rd.fiscal_year_quarter),
        epic_count: 0,
        review_status: rd.review_status,
        review_id: rd.review_id,
        final_capex_percentage: rd.final_capex_percentage,
        final_rnd_percentage: rd.final_rnd_percentage,
        assessed_epic_count: rd.assessed_epic_count,
      });
    }
  }

  return Array.from(merged.values());
}

async function fetchQuarterData(
  apiUrl: string,
  fiscalYearQuarters: string[]
): Promise<QuarterDataItem[]> {
  try {
    const [epicCounts, reviewData] = await Promise.all([
      fetchEpicCounts(apiUrl, fiscalYearQuarters),
      fetchReviewData(apiUrl, fiscalYearQuarters),
    ]);
    return mergeQuarterData(epicCounts, reviewData);
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized) {
      throw error;
    }
    console.warn('Failed to fetch quarter data from backend:', error);
    return [];
  }
}

function mapReviewStatus(
  reviewStatus: string | null,
  assessedEpicCount: number
): AllQuartersData[string]['status'] {
  if (!reviewStatus) return 'not-started';
  const status = reviewStatus.toLowerCase();
  if (status === 'active' || status === 'draft' || status === 'inprogress') {
    return assessedEpicCount > 0 ? 'inprogress' : 'not-started';
  }
  if (status === 'pendingapproval' || status === 'submitted for approval') return 'pendingapproval';
  if (status === 'approved') return 'approved';
  if (status === 'rejected') return 'rejected';
  if (status === 'archived') return 'archived';
  if (status === 'reopened' || status === 'withdrawn') return 'reopened';
  return 'not-started';
}

export function buildQuartersDataForProduct(
  products: Product[],
  projectName: string,
  fiscalYearQuarters: string[],
  quarterDataItems: QuarterDataItem[]
): AllQuartersData {
  const data: AllQuartersData = {};
  const quarterKeys = new Map(fiscalYearQuarters.map((quarter) => [canonicaliseQuarter(quarter), quarter]));
  const resolveKey = (quarter: string | null | undefined) => quarterKeys.get(canonicaliseQuarter(quarter));

  for (const item of products) {
    if (item.project_name !== projectName) continue;
    const key = resolveKey(item.fiscal_year_quarter);
    if (!key) continue;
    data[key] = {
      epicCount: 0,
      status: 'not-started',
    };
  }

  // Merge real data from the backend
  for (const qd of quarterDataItems) {
    if (qd.project_name !== projectName) continue;
    const quarter = resolveKey(qd.fiscal_year_quarter);
    if (quarter && data[quarter]) {
      data[quarter] = {
        epicCount: qd.epic_count,
        status: mapReviewStatus(qd.review_status, qd.assessed_epic_count),
        reviewId: qd.review_id ?? undefined,
        capexPercentage: qd.final_capex_percentage ?? undefined,
        rndPercentage: qd.final_rnd_percentage ?? undefined,
        hasAssessedEpics: qd.assessed_epic_count > 0,
      };
    }
  }

  return data;
}

export function hasDataForAnyQuarter(
  quartersData: AllQuartersData,
  fiscalYearQuarters: string[]
): boolean {
  return fiscalYearQuarters.some((quarter) => quartersData[quarter] !== undefined);
}

export async function getAllQuartersData(
  apiUrl: string,
  projectName: string,
  fiscalYearQuarters: string[],
  fiscalYear?: string
): Promise<AllQuartersData> {
  const [products, quarterDataItems] = await Promise.all([
    fetchAllProducts(apiUrl, fiscalYear),
    fetchQuarterData(apiUrl, fiscalYearQuarters),
  ]);
  return buildQuartersDataForProduct(products, projectName, fiscalYearQuarters, quarterDataItems);
}

export async function getMultipleProductsQuartersData(
  apiUrl: string,
  projectNames: string[],
  fiscalYearQuarters: string[],
  fiscalYear?: string
): Promise<Record<string, AllQuartersData>> {
  const [products, quarterDataItems] = await Promise.all([
    fetchAllProducts(apiUrl, fiscalYear),
    fetchQuarterData(apiUrl, fiscalYearQuarters),
  ]);
  const result: Record<string, AllQuartersData> = {};

  for (const name of projectNames) {
    result[name] = buildQuartersDataForProduct(products, name, fiscalYearQuarters, quarterDataItems);
  }

  return result;
}
